import React from "react";
import { Loader2, Check, FileText, AlignLeft } from "lucide-react";
import "./claimform-v20.css";

/*
  Coding state — NO skeleton. Coding results arrive together on `ready`, so a skeleton
  would promise progressive arrival it can't deliver. Instead the workspace shows the work:
  the five real pipeline stages, one live spinner, and the document (not the patient) in the
  header. The parent (ClaimWorkspace) swaps to the real ClaimFormV20 in one reveal on ready —
  the strip becomes the counts and the header becomes the patient, in place.
  Stage names are the pipeline's own (queued → extracting → icd_validate → rules → done);
  the live line's message is note.stage_message verbatim.
*/

const INK = "#141412";
const CANON = ["queued", "extracting", "icd_validate", "rules", "done"];
const LABEL = { queued: "Queued", extracting: "Extracting", icd_validate: "Validating ICD-10", rules: "Running rules", done: "Done" };
const DEFMSG = { queued: "waiting for a worker", extracting: "reading the document", icd_validate: "checking the documented diagnoses", rules: "screening before it goes out", done: "status ready" };
// Map the granular pipeline stage onto the five canonical steps shown to the biller.
const BUCKET = {
  queued: "queued",
  ocr: "extracting", extracting: "extracting", matching: "extracting", visit: "extracting", classifying: "extracting", validating: "extracting",
  icd_validate: "icd_validate",
  shawl: "rules", payer: "rules", modifiers: "rules", combinations: "rules", rules: "rules", billing_output: "rules",
  done: "done",
};

function fmtSize(b) {
  const n = Number(b);
  if (!n || isNaN(n)) return null;
  const kb = n / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}
function relTime(ts) {
  if (!ts) return null;
  const t = new Date(ts).getTime();
  if (isNaN(t)) return null;
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return "uploaded a moment ago";
  const m = Math.floor(s / 60);
  if (m < 60) return `uploaded ${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `uploaded ${h} h ago`;
  return `uploaded ${Math.floor(h / 24)} d ago`;
}

function bucketOf(note) {
  if (String(note?.status || "").toLowerCase() === "ready") return "done";
  const raw = String(note?.stage || "queued").toLowerCase();
  return BUCKET[raw] || "extracting";
}

export default function ClaimFormProgressive({ note, onBackground }) {
  const bucket = bucketOf(note);
  const curIdx = Math.max(0, CANON.indexOf(bucket));
  const liveMsg = note?.stage_message || DEFMSG[bucket];
  const filename = note?.filename || note?.document_name || note?.file_name || "document";
  const metaParts = [fmtSize(note?.file_size ?? note?.size ?? note?.bytes), relTime(note?.created_at || note?.uploaded_at || note?.created)].filter(Boolean);
  const meta = metaParts.length ? metaParts.join(" · ") : (note?.status ? String(note.status) : "queued");

  return (
    <div className="cfv" data-testid="claimform-progressive">
      {/* ink strip — carries the live stage; becomes the counts on ready (in ClaimFormV20) */}
      <div className="cfv-topbar" data-testid="cfp-strip">
        <span className="inline-flex items-center gap-2">
          <Loader2 size={13} className="cfv-spin" style={{ opacity: 0.9 }} />
          <span style={{ color: "rgba(255,255,255,.55)" }}>{LABEL[bucket]} ·</span>
          <span className="font-semibold" style={{ color: "#fff" }} data-testid="cfp-stage">{liveMsg}</span>
        </span>
        <span className="ml-auto font-semibold tabular-nums" style={{ color: "rgba(255,255,255,.7)" }} data-testid="cfp-count">{curIdx + 1} of {CANON.length}</span>
      </div>

      {/* header — names the DOCUMENT while coding (the patient isn't known until extraction) */}
      <div className="cfv-claimhead">
        <div className="cfv-ch-icon"><FileText size={18} /></div>
        <div className="cfv-ch-txt" data-testid="cfp-head">
          <div className="cfv-ch-eyebrow" style={{ color: "#9A5A00" }}>Coding</div>
          <div className="cfv-ch-name" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 16 }} data-testid="cfp-filename">{filename}</div>
          <div className="cfv-ch-meta">{meta}</div>
        </div>
        <button className="cfv-source" data-testid="cfp-view-source" disabled style={{ opacity: 0.4, cursor: "not-allowed" }} title="Available once the document has been read"><FileText size={13} /> View source · evidence</button>
      </div>

      {/* centered coding card — the five real stages, one live spinner */}
      <div className="cfv-body" style={{ display: "block", overflowY: "auto" }}>
        <div data-testid="cfp-card" style={{ maxWidth: 520, margin: "48px auto", border: "1px solid #E9E9EC", borderRadius: 12, background: "#fff", padding: "24px 26px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 8 }}>
            <AlignLeft size={16} style={{ color: "#9B9B97" }} />
            <span style={{ fontSize: 15, fontWeight: 800, color: INK, letterSpacing: "-.01em" }}>Coding this claim</span>
          </div>
          <p style={{ fontSize: 12.5, color: "#6C6C69", lineHeight: 1.55, margin: "0 0 20px" }}>
            revQ is reading the document against this provider's billed lines and the taxonomy corpus. The claim opens as soon as this finishes.
          </p>

          <div data-testid="cfp-stages" style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            {CANON.map((key, i) => {
              const state = i < curIdx ? "done" : i === curIdx ? "live" : "pending";
              return (
                <div key={key} data-testid={`cfp-stage-${key}`} data-state={state} style={{ display: "flex", alignItems: "center", gap: 11 }}>
                  {state === "done" ? (
                    <span style={{ width: 18, height: 18, borderRadius: 99, background: INK, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Check size={11} strokeWidth={3} color="#fff" /></span>
                  ) : state === "live" ? (
                    <Loader2 size={18} className="cfv-spin" style={{ color: "#5856D6", flex: "none" }} />
                  ) : (
                    <span style={{ width: 18, height: 18, borderRadius: 99, border: "1.5px solid #E9E9EC", flex: "none" }} />
                  )}
                  <span style={{ fontSize: 13, fontWeight: state === "pending" ? 400 : 700, color: state === "pending" ? "#9B9B97" : INK }}>{LABEL[key]}</span>
                  <span style={{ flex: 1 }} />
                  {state === "done" ? <span style={{ fontSize: 11, color: "#9B9B97" }}>done</span> : null}
                  {state === "live" ? <span style={{ fontSize: 11.5, color: "#6C6C69" }}>{liveMsg}</span> : null}
                </div>
              );
            })}
          </div>

          <p style={{ fontSize: 11.5, color: "#9B9B97", lineHeight: 1.55, margin: "22px 0 18px" }}>
            Coding runs on revQ's side, so leaving does not stop it. You will get an alert when the claim is ready.
          </p>

          <div style={{ display: "flex", justifyContent: "center" }}>
            <button data-testid="cfp-background" onClick={onBackground} style={{ fontSize: 12, fontWeight: 600, color: "#141412", background: "#fff", border: "1px solid #E9E9EC", borderRadius: 9, padding: "9px 18px", cursor: "pointer" }}>Run in the background</button>
          </div>
        </div>
      </div>

      {/* footer — Save disabled while coding; becomes the blocker count + enabled Save on ready */}
      <div className="cfv-foot">
        <span style={{ fontSize: 11.5, color: "#6E6E6B" }} data-testid="cfp-foot-hint">revQ is still coding — the claim opens when it finishes</span>
        <span style={{ flex: 1 }} />
        <button disabled data-testid="cfp-save" className="cfv-save" style={{ opacity: 0.35, cursor: "not-allowed" }}>Save claim</button>
      </div>
    </div>
  );
}
